import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { erApiFeil } from '../../api/feil.ts';
import { tilgangsendring, useAdminUsers, useAktiverAdminUser, useDeaktiverAdminUser, useLagreTilgang, type AdminUser, type RoomRole, type Romvalg } from '../../api/useAdminUsers.ts';
import { useMe } from '../../api/useMe.ts';
import { Knapp } from '../../innboks/Detalj.tsx';
import { Toast, type ToastInnhold } from '../../innboks/Toast.tsx';
import { Dialog } from '../../skall/Dialog.tsx';
import { romNavn, type Rom } from '../../skall/rom.ts';
import { useTilgang } from '../../skall/tilgang.ts';
import { useSmal } from '../../skall/useSmalSkjerm.ts';
import { Ikon } from '../../status/Ikon.tsx';
import { BareEier } from '../BareEier.tsx';
import { erUte, Setning, StatusOrd, Tilgangsvalg, Topp } from './felles.tsx';
import { erEier, grupper, GRUNN_EGEN, GRUNN_UTE, liste as ogListe, ROMROLLE, romvalgFor, setningFor } from './modell.ts';

/**
 * Personen (`/organisasjon/brukere/{id}`, M4.8 steg 3): Rolle øverst, «Tilgang per rom» som én
 * rad per rom, setningen skrevet fra valgene, og sidens egen fot med ETT primærvalg (DD-58).
 *
 * Kontrakten har ikke eget `GET /users/{id}`: siden henter lista og plukker raden, og «fant
 * ikke» er en gammel lenke, ikke en feil. «Lagre tilgangen» er ETT kall (`tilgangsendring`,
 * M4.8 steg 4): rollen og hele settet av rom, i én transaksjon. Svaret legges i lista, så siden
 * trenger ikke hente på nytt.
 *
 * ULAGREDE VALG lever i sidens minne, aldri i `utkast.tsx`: sidemenyens ulagret-tall teller dem
 * aldri, og navigering forkaster dem uten dialog (DD-65). Ctrl+S lagrer, også fra et felt.
 * Ingen bekreftelse på en tilgangsendring (DD-72 presisert): den er reversibel, logget (K7) og
 * gjelder fra neste kall — konsekvensen står som setning før knappen.
 */
export function PersonSide() {
  const tilgang = useTilgang();
  if (!tilgang.kjent) return <Laster />;
  if (!tilgang.eier) return <BareEier />;
  return <Personen />;
}

function Laster() {
  return (
    <div role="status" aria-busy="true" aria-label="Laster personen" className="panel-skjelett">
      <span className="panel-skjelett-linje panel-skjelett-tittel" />
      <span className="panel-skjelett-linje" />
    </div>
  );
}

function Personen() {
  const { id } = useParams();
  const liste = useAdminUsers(true);
  const bruker = liste.data?.find((b) => b.id === id);

  if (liste.isPending) return <Laster />;
  if (liste.isError) {
    return (
      <div className="panel-pb">
        <div role="alert" className="panel-pb-varsel">
          <Ikon navn="warning-outline" />
          <span>Kunne ikke laste panelbrukerne.</span>
          <button type="button" className="panel-knapp-sekundaer" onClick={() => void liste.refetch()}>
            Prøv igjen
          </button>
        </div>
      </div>
    );
  }
  if (!bruker) return <IkkeFunnet />;
  // Nøkkelen er personen: en annen person i samme rute starter med blanke valg.
  return <Skjema key={bruker.id} bruker={bruker} />;
}

/** En `{id}` som ikke er i lista. Ingen feilfarge — det er ikke en feil, bare en gammel lenke. */
function IkkeFunnet() {
  return (
    <div className="panel-pb panel-pb-bare">
      <h1 className="panel-h1">Fant ikke personen</h1>
      <p className="panel-tekst">Lista over panelbrukere har ingen med denne adressen. Lenken kan være gammel.</p>
      <p className="panel-tekst">
        <Link to="/organisasjon/brukere" className="panel-knapp-lenke">
          <Ikon navn="chevron-back-outline" />
          Til Panelbrukere
        </Link>
      </p>
    </div>
  );
}

function Skjema({ bruker }: { bruker: AdminUser }) {
  const me = useMe();
  const smal = useSmal();
  const lagre = useLagreTilgang();
  const aktiver = useAktiverAdminUser();
  const deaktiver = useDeaktiverAdminUser();
  const [endretEier, setEndretEier] = useState<boolean | null>(null);
  const [endretRom, setEndretRom] = useState<Romvalg>({});
  const [feil, setFeil] = useState<string | null>(null);
  const [bekreft, setBekreft] = useState(false);
  const [toast, setToast] = useState<ToastInnhold | null>(null);

  const rom = me.data?.rooms;
  const rommene: readonly Rom[] = useMemo(() => rom ?? [], [rom]);
  const flere = rommene.length > 1;
  const deg = bruker.id === me.data?.id;
  const ute = erUte(bruker);
  const laast = deg || ute;
  const grunn = deg ? GRUNN_EGEN : ute ? GRUNN_UTE : null;
  const grunnId = `pb-laas-${bruker.id}`;

  const basisEier = erEier(bruker);
  const basisRom = useMemo(() => romvalgFor(bruker), [bruker]);
  const eier = endretEier ?? basisEier;
  // Eier → medlem: rommene kommer fram med Ingen, og eier tar stilling i samme lagring.
  const valg: Romvalg = useMemo(() => Object.fromEntries(rommene.map((r) => [r, r in endretRom ? (endretRom[r] ?? null) : basisEier ? null : (basisRom[r] ?? null)])), [rommene, endretRom, basisEier, basisRom]);
  const endring = useMemo(() => (laast ? null : tilgangsendring({ eier: basisEier, rom: basisRom }, { eier, rom: valg })), [laast, basisEier, basisRom, eier, valg]);

  // Oppsummeringen er DET som lagres (DD-58): rollen hvis endret, og hvert rom som er endret.
  const endret: string[] = [];
  if (eier !== basisEier) endret.push('rollen');
  if (!eier) for (const r of rommene) if ((valg[r] ?? null) !== (basisRom[r] ?? null)) endret.push(flere ? romNavn(r) : 'tilgangen');

  const lagreNaa = useCallback(() => {
    if (laast || lagre.isPending || endring === null) return;
    setFeil(null);
    lagre
      .mutateAsync({ id: bruker.id, endring })
      .then(() => {
        setEndretEier(null);
        setEndretRom({});
        setToast({ tekst: `Tilgangen til ${bruker.email} er lagret. Den gjelder fra neste gang panelet hentes.` });
      })
      .catch((e: unknown) => {
        // To eiere som lagrer samtidig: siste lagring vinner, uten varsel og uten versjonssjekk
        // (DD-68, produkteier 2026-09-25). Klientens setning, aldri serverens — og valgene står.
        setFeil(erApiFeil(e) ? e.brukertekst : 'Kunne ikke lagre. Prøv igjen.');
      });
  }, [laast, lagre, endring, bruker.id, bruker.email]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        lagreNaa();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lagreNaa]);

  const settRom = (r: Rom, rolle: RoomRole | null) => setEndretRom((x) => ({ ...x, [r]: rolle }));

  const aktiverKonto = () =>
    aktiver
      .mutateAsync(bruker.id)
      .then(() => setToast({ tekst: `${bruker.email} har tilgang igjen.` }))
      .catch((e: unknown) => setFeil(erApiFeil(e) ? e.brukertekst : 'Noe gikk galt. Prøv igjen.'));

  const deaktiverKonto = () =>
    deaktiver
      .mutateAsync(bruker.id)
      .then(() => {
        setBekreft(false);
        setToast({ tekst: `${bruker.email} er deaktivert og mister tilgangen straks.` });
      })
      .catch((e: unknown) => {
        setBekreft(false);
        setFeil(erApiFeil(e) ? e.brukertekst : 'Noe gikk galt. Prøv igjen.');
      });

  // «Deaktiver kontoen …» er en tekstlenke, ikke rød: kontoen gjenskapes med «Aktiver» (DD-53
  // presisert, P7). Den finnes ikke på egen rad eller på en konto som alt er deaktivert.
  const deaktiverLenke = !laast ? (
    <button type="button" className="panel-knapp-lenke" onClick={() => setBekreft(true)} data-testid="pb-deaktiver">
      Deaktiver kontoen …
    </button>
  ) : null;

  const sto = grupper(basisRom, rommene)
    .map((g) => `${ROMROLLE[g.rolle].ord}${flere ? ` i ${g.rom.map(romNavn).join(', ')}` : ''}`)
    .join(' · ');

  return (
    <>
      <div className="panel-pb">
        <Topp tilbake={{ til: 'Panelbrukere', href: '/organisasjon/brukere' }} tittel={bruker.email} ingress={<StatusOrd bruker={bruker} />} />
        <div className="panel-pb-innhold panel-pb-person">
          {/* Grunnen står i DOM ÉN gang per side, over radene, og knyttes til hver gruppe med
              aria-describedby (forklart lås). */}
          {grunn ? (
            <p id={grunnId} className="panel-pb-grunn">
              <Ikon navn="lock-closed-outline" />
              <span>{grunn}</span>
            </p>
          ) : null}
          <div className="panel-pb-rader-person">
            <Tilgangsvalg eier={eier} valg={valg} rommene={rommene} onRolle={(v) => setEndretEier(v)} onRom={settRom} laast={laast} grunnId={grunnId} />
          </div>
          {!ute ? <Setning s={setningFor(bruker.email, eier, valg, rommene, false)} /> : null}
          {smal && deaktiverLenke ? <div>{deaktiverLenke}</div> : null}
        </div>
      </div>

      {feil ? (
        <div role="alert" className="panel-pb-varsel panel-pb-over-fot">
          <Ikon navn="warning-outline" />
          <span>{feil}</span>
          <button type="button" className="panel-knapp-sekundaer" onClick={() => setFeil(null)}>
            Lukk
          </button>
        </div>
      ) : null}

      <footer className="panel-innst-fot panel-pb-fot" data-testid="pb-fot">
        {ute ? (
          <>
            <Knapp label="Aktiver kontoen" primaer onClick={() => void aktiverKonto()} grunn={aktiver.isPending ? 'Aktiverer …' : null} testid="pb-aktiver" />
            <span className="panel-innst-fot-hva">Får tilgangen som sto: {sto || (flere ? 'ingen rom ennå' : 'ingen tilgang ennå')}</span>
          </>
        ) : (
          <>
            <Knapp
              label="Lagre tilgangen"
              kbd="Ctrl+S"
              primaer
              onClick={lagreNaa}
              grunn={deg ? GRUNN_EGEN : lagre.isPending ? 'Lagrer …' : endring === null ? 'Ingen endringer ennå' : null}
              grunnId={deg ? grunnId : undefined}
              testid="pb-lagre"
            />
            {endring !== null && !lagre.isPending ? (
              <span className="panel-innst-fot-hva" data-testid="pb-oppsummering">
                Endrer {ogListe(endret.length ? endret : ['tilgangen'])}
              </span>
            ) : null}
            {!smal && deaktiverLenke ? <span className="panel-pb-fot-hoyre">{deaktiverLenke}</span> : null}
          </>
        )}
      </footer>

      {bekreft ? (
        // Dialogen består: deaktivering rammer en annen person straks (DD-68). Knappen er
        // primær, ikke rød — kontoen gjenskapes med «Aktiver» (DD-53 presisert, P7).
        <Dialog tittelId="pb-deaktiver-h" onLukk={() => setBekreft(false)} testid="pb-deaktiver-dialog">
          <h2 id="pb-deaktiver-h" className="panel-h2">
            Deaktivere kontoen?
          </h2>
          <p className="panel-tekst">
            <b>{bruker.email}</b> mister tilgangen straks, også midt i en økt. Alt som er gjort fra kontoen står fortsatt i loggen med navn, og du kan aktivere kontoen igjen herfra.
          </p>
          <div className="panel-handlinger">
            <Knapp label="Deaktiver kontoen" primaer onClick={() => void deaktiverKonto()} grunn={deaktiver.isPending ? 'Deaktiverer …' : null} testid="pb-deaktiver-bekreft" />
            <Knapp label="Avbryt" kbd="Esc" fokusFørst onClick={() => setBekreft(false)} />
          </div>
        </Dialog>
      ) : null}

      {toast ? <Toast innhold={toast} onLukk={() => setToast(null)} /> : null}
    </>
  );
}
